import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Pencil, Trash, Plus, TrendUp, Receipt, Buildings, Trophy, Megaphone, PaperPlaneTilt } from "@phosphor-icons/react";

const fmtPct = (n) => `${(Number(n) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const SRC_LABEL = {
  flyer: "Panfleto no posto",
  friend: "Indicação de amigo",
  instagram: "Instagram",
  facebook: "Facebook",
  google: "Pesquisa Google",
  other: "Outra forma",
  unknown: "Sem informação",
};

export default function Marketing() {
  const [tab, setTab] = useState("stats");
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ posto: "", city: "", uf: "", quantity: "", delivered_at: new Date().toISOString().slice(0, 10), observations: "", active: true });
  const [notAdmin, setNotAdmin] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/marketing/campaigns"), api.get("/marketing/stats")]);
      setCampaigns(c.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 403) setNotAdmin(true);
      else toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ posto: "", city: "", uf: "", quantity: "", delivered_at: new Date().toISOString().slice(0, 10), observations: "", active: true });
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ posto: c.posto, city: c.city, uf: c.uf, quantity: c.quantity, delivered_at: c.delivered_at, observations: c.observations || "", active: c.active !== false });
    setOpen(true);
  };

  const save = async () => {
    if (!form.posto || !form.city || !form.uf || !form.quantity || !form.delivered_at) {
      toast.error("Preencha posto, cidade, UF, quantidade e data"); return;
    }
    try {
      const payload = { ...form, quantity: parseInt(form.quantity) || 0, uf: form.uf.toUpperCase().slice(0, 2) };
      if (editing) await api.put(`/marketing/campaigns/${editing.id}`, payload);
      else await api.post("/marketing/campaigns", payload);
      toast.success(editing ? "Entrega atualizada" : "Entrega registrada");
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remover entrega "${c.posto}"?`)) return;
    try { await api.delete(`/marketing/campaigns/${c.id}`); toast.success("Removido"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao remover"); }
  };

  if (notAdmin) {
    return (
      <Card className="border-2 border-amber-300 bg-amber-50">
        <CardContent className="p-6 text-amber-900 font-semibold" data-testid="marketing-forbidden">
          Esta área é restrita ao administrador do app.
        </CardContent>
      </Card>
    );
  }

  if (loading && !stats) return <p className="text-muted-foreground">Carregando…</p>;

  return (
    <div className="space-y-6" data-testid="marketing-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-fuchsia-900 via-rose-800 to-amber-700 p-6 md:p-8 shadow-2xl text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fde047 0%, transparent 40%)" }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-amber-300 text-xs font-bold uppercase tracking-widest">
            <Megaphone size={16} weight="fill" /> Marketing & Divulgação
          </div>
          <h1 className="text-3xl md:text-5xl font-black mt-1 tracking-tight" style={{ fontFamily: "Manrope" }}>
            Qual posto trouxe meus clientes?
          </h1>
          <p className="text-white/90 text-sm mt-2 max-w-xl">
            Cadastre os panfletos entregues em cada posto. Quando o motorista se cadastrar, você vai saber de onde veio.
          </p>
        </div>
      </div>

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card data-testid="mkt-total-signups" className="border-2 border-fuchsia-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Receipt size={12} /> Cadastros no total</div>
              <div className="text-3xl font-black mt-1 text-fuchsia-700" style={{ fontFamily: "Manrope" }}>{stats.total_signups}</div>
            </CardContent>
          </Card>
          <Card data-testid="mkt-total-paying" className="border-2 border-emerald-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><TrendUp size={12} /> Assinantes pagantes</div>
              <div className="text-3xl font-black mt-1 text-emerald-700" style={{ fontFamily: "Manrope" }}>{stats.total_paying}</div>
            </CardContent>
          </Card>
          <Card data-testid="mkt-total-conversion" className="border-2 border-amber-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Trophy size={12} /> Conversão geral</div>
              <div className="text-3xl font-black mt-1 text-amber-700" style={{ fontFamily: "Manrope" }}>
                {stats.total_signups > 0 ? fmtPct((stats.total_paying / stats.total_signups) * 100) : "—"}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="mkt-total-campaigns" className="border-2 border-blue-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Buildings size={12} /> Postos ativos</div>
              <div className="text-3xl font-black mt-1 text-blue-700" style={{ fontFamily: "Manrope" }}>{campaigns.filter(c => c.active !== false).length}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2">
        {[{ k: "stats", l: "📊 Resultados" }, { k: "campaigns", l: "📋 Entregas de panfleto" }].map((t) => (
          <Button key={t.k} data-testid={`mkt-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={tab === t.k ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}>{t.l}</Button>
        ))}
      </div>

      {/* STATS TAB */}
      {tab === "stats" && stats && (
        <>
          <Card data-testid="mkt-sources">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><PaperPlaneTilt size={20} weight="duotone" className="text-fuchsia-600" /> De onde vêm os cadastros</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {stats.sources.length === 0 ? <p className="text-sm text-slate-500">Nenhum cadastro ainda.</p> : stats.sources.map((s) => (
                <div key={s.source} className="flex items-center gap-3 border rounded-lg p-3 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="font-bold text-slate-900">{SRC_LABEL[s.source] || s.source}</div>
                    <div className="text-xs text-slate-500">{s.signups} cadastro(s) · {s.paying} pagante(s)</div>
                  </div>
                  <div className="text-xl font-black text-emerald-700" style={{ fontFamily: "Manrope" }}>
                    {s.signups > 0 ? fmtPct((s.paying / s.signups) * 100) : "—"}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card data-testid="mkt-top-postos">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Trophy size={20} weight="duotone" className="text-amber-600" /> TOP Postos convertendo</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {stats.campaigns.length === 0 ? <p className="text-sm text-slate-500">Cadastre entregas de panfleto para começar.</p> : stats.campaigns.slice(0, 10).map((c, i) => (
                <div key={c.id} data-testid={`mkt-posto-${c.id}`} className="flex items-center gap-3 border rounded-lg p-3 flex-wrap bg-white">
                  <div className={`w-9 h-9 rounded-full font-black flex items-center justify-center text-white ${i === 0 ? "bg-amber-400" : i === 1 ? "bg-slate-400" : i === 2 ? "bg-orange-400" : "bg-slate-500"}`}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-bold text-slate-900">{c.posto}</div>
                    <div className="text-xs text-slate-500">{c.city}/{c.uf} · {c.quantity} panfletos · entregue em {c.delivered_at}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-slate-600"><strong>{c.signups}</strong> cad. · <strong className="text-emerald-700">{c.paying}</strong> pag.</div>
                    <div className="text-xs text-slate-500">conv.: {fmtPct(c.conversion_rate)}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}

      {/* CAMPAIGNS TAB */}
      {tab === "campaigns" && (
        <Card data-testid="mkt-campaigns-list">
          <CardHeader className="flex-row items-center justify-between gap-2">
            <CardTitle className="text-lg flex items-center gap-2"><MapPin size={20} weight="duotone" className="text-fuchsia-600" /> Entregas registradas</CardTitle>
            <Button onClick={openNew} data-testid="mkt-new-campaign" className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white"><Plus size={16} className="mr-1" /> Nova entrega</Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {campaigns.length === 0 && <p className="text-sm text-slate-500">Nenhuma entrega cadastrada. Clique em "Nova entrega" para começar.</p>}
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`mkt-row-${c.id}`} className={`flex items-center gap-3 border rounded-lg p-3 flex-wrap ${c.active !== false ? "bg-white" : "bg-slate-100 opacity-70"}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="font-bold text-slate-900">{c.posto} {c.active === false && <span className="text-xs font-normal text-slate-500">(inativo)</span>}</div>
                  <div className="text-xs text-slate-600">{c.city}/{c.uf} · {c.quantity} panfletos · {c.delivered_at}</div>
                  {c.observations && <div className="text-xs text-slate-500 mt-1 italic">"{c.observations}"</div>}
                </div>
                <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`mkt-edit-${c.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="destructive" onClick={() => remove(c)} data-testid={`mkt-del-${c.id}`}><Trash size={14} /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Dialog Form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mkt-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Editar entrega" : "Nova entrega de panfleto"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Posto (nome e localização) *</Label><Input data-testid="mkt-input-posto" value={form.posto} onChange={e => setForm({...form, posto: e.target.value})} placeholder="Ex: Posto Shell BR-364 km 50" /></div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2"><Label>Cidade *</Label><Input data-testid="mkt-input-city" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Vilhena" /></div>
              <div><Label>UF *</Label><Input data-testid="mkt-input-uf" value={form.uf} maxLength={2} onChange={e => setForm({...form, uf: e.target.value.toUpperCase()})} placeholder="RO" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Quantidade *</Label><Input data-testid="mkt-input-qty" type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} placeholder="50" /></div>
              <div><Label>Data entrega *</Label><Input data-testid="mkt-input-date" type="date" value={form.delivered_at} onChange={e => setForm({...form, delivered_at: e.target.value})} /></div>
            </div>
            <div><Label>Observação</Label><Input data-testid="mkt-input-obs" value={form.observations} onChange={e => setForm({...form, observations: e.target.value})} placeholder="Deixado no caixa, com dono do posto, etc." /></div>
            <div className="flex items-center gap-3">
              <Switch data-testid="mkt-input-active" checked={form.active} onCheckedChange={v => setForm({...form, active: v})} />
              <Label>Ativo (aparece no dropdown de cadastro)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="mkt-save" className="bg-fuchsia-700 hover:bg-fuchsia-800 text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TelegramPanel />
    </div>
  );
}

function TelegramPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/telegram/status"); setStatus(data); } catch (_) {}
  };
  useEffect(() => { load(); }, []);

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/telegram/test");
      if (data?.sent) toast.success("Mensagem enviada! Confira seu Telegram.");
      else toast.error("Não enviou. Verifique se o TOKEN e o CHAT_ID estão configurados.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro no envio"); }
    finally { setTesting(false); }
  };

  const discoverChatIds = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/telegram/discover");
      if (!data?.chats?.length) {
        toast.info("Nenhuma conversa encontrada. Envie /start para o bot primeiro.");
      } else {
        const txt = data.chats.map((c, i) => `${i + 1}. ${c.name} — chat_id: ${c.chat_id}`).join("\n");
        alert("Conversas encontradas:\n\n" + txt + "\n\nCopie o chat_id correto e me envie no chat do Emergent para que eu configure.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
    finally { setLoading(false); }
  };

  if (!status) return null;
  const connected = status.bot_configured && status.chat_id_configured;

  return (
    <Card data-testid="mkt-telegram-panel" className={`border-2 ${connected ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <PaperPlaneTilt size={20} weight="duotone" className={connected ? "text-emerald-700" : "text-amber-700"} />
          Notificações no Telegram
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-sm">
          <strong>Status:</strong>{" "}
          {connected ? (
            <span className="text-emerald-700 font-bold">✅ Conectado — você vai receber alertas no Telegram</span>
          ) : status.bot_configured ? (
            <span className="text-amber-700 font-bold">⚠️ Bot ok, mas falta o chat_id do admin</span>
          ) : (
            <span className="text-rose-700 font-bold">❌ Bot não configurado — envie o TOKEN pro E1</span>
          )}
        </div>
        {!connected && status.bot_configured && (
          <div className="text-xs text-slate-700 bg-white p-3 rounded border">
            👉 1) Abra o Telegram → procure seu bot → envie <code className="bg-slate-100 px-1 rounded">/start</code><br />
            👉 2) Clique em "Buscar chat_ids" abaixo
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button onClick={sendTest} disabled={testing || !connected} data-testid="tg-send-test" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            {testing ? "Enviando..." : "📨 Enviar teste"}
          </Button>
          <Button onClick={discoverChatIds} disabled={loading || !status.bot_configured} variant="outline" data-testid="tg-discover">
            {loading ? "Buscando..." : "🔍 Buscar chat_ids"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
